import React, { useState, useRef } from "react";
import { Formik, Form } from "formik";
import { Button, FormikInput } from "../../../ui";
import formStyles from "../addNewLocationForm/style.module.scss";
import componentStyles from "./style.module.scss";
import { handleApiErrors, showToast } from "../../../../utils/helpers";
import jwtInterceoptor from "../../../../api/jwtInterceptor";
import { X, InfoCircle } from "react-bootstrap-icons";

const initialValues = {
  title: "",
  body: "",
  photo: null,
  date: "",
  time: "",
};

const PUSH_NOTIFICATION_URL = "/api/admin/notifications/schedule/";

const validate = (values) => {
  const errors = {};
  if (!values.title) errors.title = "Title is required";
  if (!values.body) errors.body = "Body Text is required";
  // if (!values.photo) errors.photo = "Photo is required";
  if (!values.date) errors.date = "Date scheduled is required";
  if (!values.time) errors.time = "Time scheduled is required";
  return errors;
};

const AddPushNotificationForm = (props) => {
  const [uploadName, setUploadName] = useState("Photo");
  const fileInputRef = useRef(null);

  const handlePhotoChange = (e, setFieldValue) => {
    const file = e.currentTarget.files[0];
    if (file) {
      setFieldValue("photo", file);
      setUploadName(file.name);
    }
  };

  const handleRemovePhoto = (setFieldValue) => {
    setFieldValue("photo", null);
    setUploadName("Photo");

    // Reset the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = (formikProps) => {
    // Reset Formik form
    formikProps.resetForm();

    // Reset local state
    setUploadName("Photo");

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Formik
      initialValues={props.formInitialValues || initialValues}
      validate={validate}
      enableReinitialize={true}
      onSubmit={async (values, formikProps) => {
        props.setLoading(true);
        props.setError(false);
        try {
          // Combine date and time for scheduled_at
          const scheduledDateTime =
            values.date && values.time
              ? `${values.date}T${values.time}:00.000Z`
              : new Date().toISOString();

          const payload = {
            caption: values.title,
            message: values.body,
            image: values.photo ? "string" : "", // Placeholder for image
            scheduled_at: scheduledDateTime,
            all_users: true,
          };

          const formData = new FormData();
          // Add all fields to formData
          Object.entries(payload).forEach(([key, value]) => {
            if (key !== "image") {
              // Skip the image placeholder
              formData.append(key, value);
            }
          });

          // Add the photo file to formData
          if (values.photo) {
            formData.append("image", values.photo);
          }

          await jwtInterceoptor.post(PUSH_NOTIFICATION_URL, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          props.setLoading(false);

          // Reset form completely including file input
          resetForm(formikProps);

          showToast("Push notification scheduled successfully", "success");

          // Call onSuccess callback if provided to refresh the list
          if (props.onSuccess && typeof props.onSuccess === "function") {
            props.onSuccess();
          }
        } catch (error) {
          props.setLoading(false);
          const errorsData = [];
          // Handle errors
          const errorMessages = handleApiErrors(error);
          // Display each error in a separate snackbar
          errorMessages.forEach(({ field, message }) => {
            const formattedMessage = field ? `${field}: ${message}` : message;
            errorsData.push(formattedMessage);
          });
          props.setError(errorsData);
        }
      }}
    >
      {({ errors, touched, getFieldProps, setFieldValue, values }) => (
        <Form className={formStyles.addLocationForm}>
          <section>
            <div>
              <FormikInput
                type="text"
                name="title"
                label="Title"
                formikProps={getFieldProps("title")}
                formikTouch={touched.title}
                formikErrors={errors.title}
                style={
                  touched.title && errors.title ? { borderColor: "red" } : null
                }
              />
            </div>
            <div>
              <FormikInput
                type="text"
                name="body"
                label="Body Text"
                formikProps={getFieldProps("body")}
                formikTouch={touched.body}
                formikErrors={errors.body}
                style={
                  touched.body && errors.body ? { borderColor: "red" } : null
                }
                multiline
                rows={4}
              />
            </div>
            <div>
              {/* Custom Photo Upload Implementation */}
              <div className="uploadLogoContainer">
                <div className="uploadContainer">
                  <input
                    type="file"
                    id="photo"
                    name="photo"
                    accept="image/*"
                    hidden
                    ref={fileInputRef}
                    onChange={(e) => handlePhotoChange(e, setFieldValue)}
                  />

                  <label htmlFor="photo">UPLOAD</label>
                  <span>{uploadName}</span>

                  {values.photo && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(setFieldValue)}
                      className={componentStyles.removePhotoBtn}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {touched.photo && errors.photo ? (
                  <span className="errorText">{errors.photo}</span>
                ) : null}
              </div>
            </div>
            <div>
              <FormikInput
                type="date"
                name="date"
                label="Date scheduled"
                formikProps={getFieldProps("date")}
                formikTouch={touched.date}
                formikErrors={errors.date}
                style={
                  touched.date && errors.date ? { borderColor: "red" } : null
                }
              />
              <FormikInput
                type="time"
                name="time"
                label="Time scheduled"
                formikProps={getFieldProps("time")}
                formikTouch={touched.time}
                formikErrors={errors.time}
                style={
                  touched.time && errors.time ? { borderColor: "red" } : null
                }
              />
            </div>

            {/* Timezone Note */}
            <div className={componentStyles.timezoneNote}>
              <InfoCircle size={18} className={componentStyles.infoIcon} />
              <span>
                All notifications will be scheduled according to PST Timezone
              </span>
            </div>

            <div>
              <Button
                type="submit"
                label={props.loading ? "Sending..." : "Send Notification"}
                disabled={props.loading}
                primary
                style={{ maxWidth: "20rem", margin: "0 0 0 auto" }}
              />
            </div>
          </section>
        </Form>
      )}
    </Formik>
  );
};

export default AddPushNotificationForm;
